/**
 * PendingBookings Component
 * 
 * Shows reception staff:
 * 1. Confirmed appointments that need to be booked in Gensolve
 * 2. Patients who completed forms but haven't been sent slots yet
 * 
 * Add this to your DashboardPage.js or create as a separate page
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Calendar,
  Clock,
  User,
  Phone,
  PhoneCall,
  Mail,
  CheckCircle,
  Send,
  AlertCircle,
  ExternalLink,
  RefreshCw,
  Ban,
  Trash2,
  Plus,
  Repeat
} from 'lucide-react';
import API_URL from '../config/api';
import { useClinic } from '../contexts/ClinicContext';

// Gensolve-style colors
const colors = {
  sidebarBg: '#e8941c',
  headerBg: '#f5f5f5',
  dialogBg: '#6b9bd1',
  dialogBody: '#b8d4f0',
  success: '#5cb85c',
  warning: '#f0ad4e',
  danger: '#d9534f',
};

function PendingBookings() {
  const { activeClinicId } = useClinic();
  const clinicQuery = activeClinicId ? `?clinicId=${activeClinicId}` : '';
  const [pendingBookings, setPendingBookings] = useState([]);
  const [awaitingProposal, setAwaitingProposal] = useState([]);
  const [telehealthDeclined, setTelehealthDeclined] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState({});

  // Blocked times state
  const [blockedTimes, setBlockedTimes] = useState([]);
  const [blockForm, setBlockForm] = useState({
    date: '',
    startTime: '14:00',
    endTime: '14:30',
    reason: '',
    recurring: false,
    dayOfWeek: 1,
  });
  const [blockSubmitting, setBlockSubmitting] = useState(false);

  const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  useEffect(() => {
    fetchData();
  }, [activeClinicId]);

  const fetchData = async () => {
    setLoading(true);
    setError('');

    try {
      const [pendingRes, awaitingRes, declinedRes] = await Promise.all([
        axios.get(`${API_URL}/booking/pending${clinicQuery}`),
        axios.get(`${API_URL}/booking/awaiting-proposal${clinicQuery}`),
        axios.get(`${API_URL}/booking/telehealth-declined${clinicQuery}`),
      ]);

      setPendingBookings(pendingRes.data.bookings || []);
      setAwaitingProposal(awaitingRes.data.patients || []);
      setTelehealthDeclined(declinedRes.data.patients || []);
    } catch (err) {
      console.error('Error fetching booking data:', err);
      setError('Failed to load booking data');
    } finally {
      setLoading(false);
    }

    // Fetch blocked times separately so a failure doesn't break the rest of the page
    try {
      const blockedRes = await axios.get(`${API_URL}/booking/blocked-times`);
      setBlockedTimes(blockedRes.data.blockedTimes || []);
    } catch (err) {
      console.warn('Failed to load blocked times:', err);
    }
  };

  const handleSendSlots = async (patientId) => {
    setActionLoading(prev => ({ ...prev, [patientId]: 'sending' }));
    
    try {
      await axios.post(`${API_URL}/booking/propose/${patientId}`);
      
      // Refresh data
      await fetchData();
      
      alert('Appointment slots sent to patient!');
    } catch (err) {
      console.error('Error sending slots:', err);
      alert(err.response?.data?.error || 'Failed to send appointment slots');
    } finally {
      setActionLoading(prev => ({ ...prev, [patientId]: null }));
    }
  };

  const handleMarkBooked = async (patientId) => {
    if (!window.confirm('Have you booked this appointment in Gensolve?')) {
      return;
    }
    
    setActionLoading(prev => ({ ...prev, [patientId]: 'marking' }));
    
    try {
      await axios.post(`${API_URL}/booking/mark-booked/${patientId}`);
      
      // Refresh data
      await fetchData();
      
    } catch (err) {
      console.error('Error marking as booked:', err);
      alert('Failed to mark as booked');
    } finally {
      setActionLoading(prev => ({ ...prev, [patientId]: null }));
    }
  };

  const handleMarkContacted = async (patientId) => {
    setActionLoading(prev => ({ ...prev, [patientId]: 'contacting' }));

    try {
      await axios.post(`${API_URL}/booking/mark-contacted/${patientId}`);
      await fetchData();
    } catch (err) {
      console.error('Error marking as contacted:', err);
      alert('Failed to mark as contacted');
    } finally {
      setActionLoading(prev => ({ ...prev, [patientId]: null }));
    }
  };

  const handleAddBlockedTime = async (e) => {
    e.preventDefault();
    setBlockSubmitting(true);

    try {
      let startTime, endTime;

      if (blockForm.recurring) {
        // For recurring: store time-of-day using an arbitrary reference date
        // Use browser's local timezone (NZ) so the server stores correct UTC
        startTime = new Date(`2000-01-01T${blockForm.startTime}:00`).toISOString();
        endTime = new Date(`2000-01-01T${blockForm.endTime}:00`).toISOString();
      } else {
        if (!blockForm.date) {
          alert('Please select a date');
          setBlockSubmitting(false);
          return;
        }
        // Convert local NZ time to UTC ISO string so the server stores the correct time
        startTime = new Date(`${blockForm.date}T${blockForm.startTime}:00`).toISOString();
        endTime = new Date(`${blockForm.date}T${blockForm.endTime}:00`).toISOString();
      }

      await axios.post(`${API_URL}/booking/blocked-times`, {
        startTime,
        endTime,
        reason: blockForm.reason || undefined,
        recurring: blockForm.recurring,
        dayOfWeek: blockForm.recurring ? blockForm.dayOfWeek : undefined,
      });

      setBlockForm({ date: '', startTime: '14:00', endTime: '14:30', reason: '', recurring: false, dayOfWeek: 1 });
      await fetchData();
    } catch (err) {
      console.error('Error creating blocked time:', err);
      alert(err.response?.data?.error || 'Failed to create blocked time');
    } finally {
      setBlockSubmitting(false);
    }
  };

  const handleDeleteBlockedTime = async (id) => {
    if (!window.confirm('Remove this blocked time? Slots will become available again.')) return;

    try {
      await axios.delete(`${API_URL}/booking/blocked-times/${id}`);
      await fetchData();
    } catch (err) {
      console.error('Error deleting blocked time:', err);
      alert('Failed to delete blocked time');
    }
  };

  const formatBlockedTime = (bt) => {
    const opts = { hour: 'numeric', minute: '2-digit', hour12: true };
    const start = new Date(bt.startTime);
    const end = new Date(bt.endTime);

    if (bt.recurring) {
      return `Every ${DAY_NAMES[bt.dayOfWeek]}, ${start.toLocaleTimeString('en-NZ', opts)} - ${end.toLocaleTimeString('en-NZ', opts)}`;
    }
    return `${start.toLocaleDateString('en-NZ', { weekday: 'short', day: 'numeric', month: 'short' })} ${start.toLocaleTimeString('en-NZ', opts)} - ${end.toLocaleTimeString('en-NZ', opts)}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-NZ', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  if (loading) {
    return (
      <div style={{ padding: 20, textAlign: 'center' }}>
        <RefreshCw className="animate-spin" size={24} />
        <p>Loading bookings...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 20 }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: 20 
      }}>
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Calendar size={24} />
          Appointment Bookings
        </h2>
        <button
          onClick={fetchData}
          style={{
            padding: '8px 16px',
            backgroundColor: colors.dialogBg,
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {error && (
        <div style={{ 
          backgroundColor: '#fee', 
          color: colors.danger, 
          padding: 15, 
          borderRadius: 4,
          marginBottom: 20 
        }}>
          {error}
        </div>
      )}

      {/* Section 1: Confirmed - Ready to Book in Gensolve */}
      <div style={{ marginBottom: 30 }}>
        <h3 style={{ 
          backgroundColor: colors.success, 
          color: 'white', 
          padding: '10px 15px',
          borderRadius: '4px 4px 0 0',
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <CheckCircle size={20} />
          Ready to Book in Gensolve ({pendingBookings.length})
        </h3>
        
        <div style={{ 
          border: `1px solid ${colors.success}`, 
          borderTop: 'none',
          borderRadius: '0 0 4px 4px',
        }}>
          {pendingBookings.length === 0 ? (
            <p style={{ padding: 20, textAlign: 'center', color: '#666' }}>
              No confirmed appointments waiting to be booked.
            </p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Patient</th>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Appointment</th>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Contact</th>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Details</th>
                  <th style={{ padding: 12, textAlign: 'center', borderBottom: '1px solid #ddd' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {pendingBookings.map(booking => (
                  <tr key={booking.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: 12 }}>
                      <div style={{ fontWeight: 'bold' }}>{booking.name}</div>
                      <div style={{ fontSize: 12, color: '#666' }}>
                        DOB: {booking.dateOfBirth || 'N/A'}
                      </div>
                      {booking.nhi && (
                        <div style={{ fontSize: 12, color: '#666' }}>
                          NHI: {booking.nhi}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: 12 }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 8,
                        fontWeight: 'bold',
                        color: colors.success,
                      }}>
                        <Calendar size={16} />
                        {booking.slotFormatted || formatDateTime(booking.selectedSlot)}
                      </div>
                      <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                        Confirmed: {formatDateTime(booking.confirmedAt)}
                      </div>
                    </td>
                    <td style={{ padding: 12 }}>
                      {booking.phone && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          <Phone size={14} />
                          <a href={`tel:${booking.phone}`}>{booking.phone}</a>
                        </div>
                      )}
                      {booking.email && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Mail size={14} />
                          <a href={`mailto:${booking.email}`} style={{ fontSize: 12 }}>
                            {booking.email}
                          </a>
                        </div>
                      )}
                    </td>
                    <td style={{ padding: 12 }}>
                      {booking.injuryType && (
                        <div style={{ fontSize: 12, marginBottom: 4 }}>
                          <strong>Injury:</strong> {booking.injuryType}
                        </div>
                      )}
                      {booking.accNumber && (
                        <div style={{ fontSize: 12, marginBottom: 4 }}>
                          <strong>ACC:</strong> {booking.accNumber}
                        </div>
                      )}
                      {booking.gensolveClientId && (
                        <div style={{ fontSize: 12, color: colors.success }}>
                          ✓ In Gensolve: {booking.gensolveClientId}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: 12, textAlign: 'center' }}>
                      <button
                        onClick={() => handleMarkBooked(booking.id)}
                        disabled={actionLoading[booking.id]}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: colors.success,
                          color: 'white',
                          border: 'none',
                          borderRadius: 4,
                          cursor: actionLoading[booking.id] ? 'not-allowed' : 'pointer',
                          opacity: actionLoading[booking.id] ? 0.7 : 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          margin: '0 auto',
                        }}
                      >
                        <CheckCircle size={16} />
                        {actionLoading[booking.id] === 'marking' ? 'Saving...' : 'Mark Booked'}
                      </button>
                      <a
                        href={`/patients/${booking.id}`}
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 4,
                          marginTop: 8,
                          fontSize: 12,
                          color: colors.dialogBg,
                          justifyContent: 'center',
                        }}
                      >
                        <ExternalLink size={12} />
                        View Patient
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Section 2: Awaiting Slot Proposal */}
      <div style={{ marginBottom: 30 }}>
        <h3 style={{
          backgroundColor: colors.warning,
          color: 'white',
          padding: '10px 15px',
          borderRadius: '4px 4px 0 0',
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <Clock size={20} />
          Forms Completed - Send Appointment Options ({awaitingProposal.length})
        </h3>

        <div style={{
          border: `1px solid ${colors.warning}`,
          borderTop: 'none',
          borderRadius: '0 0 4px 4px',
        }}>
          {awaitingProposal.length === 0 ? (
            <p style={{ padding: 20, textAlign: 'center', color: '#666' }}>
              No patients waiting for appointment options.
            </p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Patient</th>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Form Completed</th>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Contact</th>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Injury</th>
                  <th style={{ padding: 12, textAlign: 'center', borderBottom: '1px solid #ddd' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {awaitingProposal.map(patient => (
                  <tr key={patient.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: 12 }}>
                      <div style={{ fontWeight: 'bold' }}>{patient.name}</div>
                    </td>
                    <td style={{ padding: 12 }}>
                      {formatDateTime(patient.formCompletedAt)}
                    </td>
                    <td style={{ padding: 12 }}>
                      {patient.phone && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          <Phone size={14} />
                          {patient.phone}
                        </div>
                      )}
                      {patient.email && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Mail size={14} />
                          <span style={{ fontSize: 12 }}>{patient.email}</span>
                        </div>
                      )}
                    </td>
                    <td style={{ padding: 12 }}>
                      {patient.injuryType || 'N/A'}
                    </td>
                    <td style={{ padding: 12, textAlign: 'center' }}>
                      <button
                        onClick={() => handleSendSlots(patient.id)}
                        disabled={actionLoading[patient.id] || !patient.email}
                        title={!patient.email ? 'Patient has no email address' : ''}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: patient.email ? colors.sidebarBg : '#ccc',
                          color: 'white',
                          border: 'none',
                          borderRadius: 4,
                          cursor: actionLoading[patient.id] || !patient.email ? 'not-allowed' : 'pointer',
                          opacity: actionLoading[patient.id] ? 0.7 : 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          margin: '0 auto',
                        }}
                      >
                        <Send size={16} />
                        {actionLoading[patient.id] === 'sending' ? 'Sending...' : 'Send Slots'}
                      </button>
                      {!patient.email && (
                        <div style={{ fontSize: 11, color: colors.danger, marginTop: 4 }}>
                          <AlertCircle size={12} style={{ display: 'inline', marginRight: 4 }} />
                          No email
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Section 3: Telehealth Declined - Contact for In-Person */}
      <div style={{ marginBottom: 30 }}>
        <h3 style={{
          backgroundColor: colors.danger,
          color: 'white',
          padding: '10px 15px',
          borderRadius: '4px 4px 0 0',
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <PhoneCall size={20} />
          Telehealth Declined - Contact for In-Person Appointment ({telehealthDeclined.length})
        </h3>

        <div style={{
          border: `1px solid ${colors.danger}`,
          borderTop: 'none',
          borderRadius: '0 0 4px 4px',
        }}>
          {telehealthDeclined.length === 0 ? (
            <p style={{ padding: 20, textAlign: 'center', color: '#666' }}>
              No patients awaiting in-person booking.
            </p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Patient</th>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Form Completed</th>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Contact</th>
                  <th style={{ padding: 12, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Injury</th>
                  <th style={{ padding: 12, textAlign: 'center', borderBottom: '1px solid #ddd' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {telehealthDeclined.map(patient => (
                  <tr key={patient.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: 12 }}>
                      <div style={{ fontWeight: 'bold' }}>{patient.name}</div>
                    </td>
                    <td style={{ padding: 12 }}>
                      {formatDateTime(patient.formCompletedAt)}
                    </td>
                    <td style={{ padding: 12 }}>
                      {patient.phone && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          <Phone size={14} />
                          <a href={`tel:${patient.phone}`}>{patient.phone}</a>
                        </div>
                      )}
                      {patient.email && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Mail size={14} />
                          <a href={`mailto:${patient.email}`} style={{ fontSize: 12 }}>
                            {patient.email}
                          </a>
                        </div>
                      )}
                    </td>
                    <td style={{ padding: 12 }}>
                      {patient.injuryType || 'N/A'}
                    </td>
                    <td style={{ padding: 12, textAlign: 'center' }}>
                      <button
                        onClick={() => handleMarkContacted(patient.id)}
                        disabled={actionLoading[patient.id]}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: colors.danger,
                          color: 'white',
                          border: 'none',
                          borderRadius: 4,
                          cursor: actionLoading[patient.id] ? 'not-allowed' : 'pointer',
                          opacity: actionLoading[patient.id] ? 0.7 : 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          margin: '0 auto',
                        }}
                      >
                        <CheckCircle size={16} />
                        {actionLoading[patient.id] === 'contacting' ? 'Saving...' : 'Mark Contacted'}
                      </button>
                      <a
                        href={`/patients/${patient.id}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                          marginTop: 8,
                          fontSize: 12,
                          color: colors.dialogBg,
                          justifyContent: 'center',
                        }}
                      >
                        <ExternalLink size={12} />
                        View Patient
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Section 4: Blocked Times */}
      <div>
        <h3 style={{
          backgroundColor: colors.dialogBg,
          color: 'white',
          padding: '10px 15px',
          borderRadius: '4px 4px 0 0',
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <Ban size={20} />
          Blocked Times ({blockedTimes.length})
        </h3>

        <div style={{
          border: `1px solid ${colors.dialogBg}`,
          borderTop: 'none',
          borderRadius: '0 0 4px 4px',
          padding: 20,
        }}>
          {/* Add form */}
          <form onSubmit={handleAddBlockedTime} style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            alignItems: 'flex-end',
            marginBottom: 20,
            padding: 15,
            backgroundColor: '#f8f9fa',
            borderRadius: 4,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={blockForm.recurring}
                  onChange={(e) => setBlockForm(f => ({ ...f, recurring: e.target.checked }))}
                />
                <Repeat size={14} />
                Recurring weekly
              </label>
            </div>

            {blockForm.recurring ? (
              <div>
                <label style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Day of Week</label>
                <select
                  value={blockForm.dayOfWeek}
                  onChange={(e) => setBlockForm(f => ({ ...f, dayOfWeek: parseInt(e.target.value, 10) }))}
                  style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid #ccc' }}
                >
                  {DAY_NAMES.map((name, i) => (
                    <option key={i} value={i}>{name}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Date</label>
                <input
                  type="date"
                  value={blockForm.date}
                  onChange={(e) => setBlockForm(f => ({ ...f, date: e.target.value }))}
                  style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid #ccc' }}
                />
              </div>
            )}

            <div>
              <label style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Start Time</label>
              <input
                type="time"
                value={blockForm.startTime}
                onChange={(e) => setBlockForm(f => ({ ...f, startTime: e.target.value }))}
                style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid #ccc' }}
              />
            </div>

            <div>
              <label style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>End Time</label>
              <input
                type="time"
                value={blockForm.endTime}
                onChange={(e) => setBlockForm(f => ({ ...f, endTime: e.target.value }))}
                style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid #ccc' }}
              />
            </div>

            <div>
              <label style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Reason (optional)</label>
              <input
                type="text"
                value={blockForm.reason}
                onChange={(e) => setBlockForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="e.g. Team meeting"
                style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid #ccc', width: 180 }}
              />
            </div>

            <button
              type="submit"
              disabled={blockSubmitting}
              style={{
                padding: '8px 16px',
                backgroundColor: colors.dialogBg,
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor: blockSubmitting ? 'not-allowed' : 'pointer',
                opacity: blockSubmitting ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Plus size={16} />
              {blockSubmitting ? 'Adding...' : 'Add Block'}
            </button>
          </form>

          {/* List */}
          {blockedTimes.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#666' }}>
              No blocked times configured. Slots are generated based on the standard schedule.
            </p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f5f5f5' }}>
                  <th style={{ padding: 10, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Type</th>
                  <th style={{ padding: 10, textAlign: 'left', borderBottom: '1px solid #ddd' }}>When</th>
                  <th style={{ padding: 10, textAlign: 'left', borderBottom: '1px solid #ddd' }}>Reason</th>
                  <th style={{ padding: 10, textAlign: 'center', borderBottom: '1px solid #ddd' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {blockedTimes.map(bt => (
                  <tr key={bt.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: 10 }}>
                      {bt.recurring ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: colors.dialogBg }}>
                          <Repeat size={14} /> Recurring
                        </span>
                      ) : (
                        <span style={{ color: '#666' }}>One-off</span>
                      )}
                    </td>
                    <td style={{ padding: 10, fontWeight: 500 }}>
                      {formatBlockedTime(bt)}
                    </td>
                    <td style={{ padding: 10, color: '#666' }}>
                      {bt.reason || '-'}
                    </td>
                    <td style={{ padding: 10, textAlign: 'center' }}>
                      <button
                        onClick={() => handleDeleteBlockedTime(bt.id)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: colors.danger,
                          color: 'white',
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        <Trash2 size={14} />
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default PendingBookings;
